import React from 'react';
import { Container, Typography, Box, Card, CardContent, CardMedia, Button, Divider } from '@mui/material';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import PeopleIcon from '@mui/icons-material/People';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CellTowerIcon from '@mui/icons-material/CellTower';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const OtherServices: React.FC = () => {
  const services = [
    {
      id: 'data-entry',
      title: 'Data Entry Services',
      description: 'Professional data entry services to help organizations manage their information efficiently.',
      icon: <KeyboardIcon sx={{ fontSize: 60, color: '#1976d2' }} />,
      imageUrl: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80',
      link: '/services/other/data-entry'
    },
    {
      id: 'hr-outsourcing',
      title: 'HR Outsourcing Services',
      description: 'Comprehensive HR solutions to help you manage your workforce effectively.',
      icon: <PeopleIcon sx={{ fontSize: 60, color: '#2e7d32' }} />,
      imageUrl: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=1374&q=80',
      link: '/services/other/hr-outsourcing'
    },
    {
      id: 'research',
      title: 'Research & Development',
      description: 'Innovative R&D services to help you stay ahead of the competition.',
      icon: <LightbulbIcon sx={{ fontSize: 60, color: '#ed6c02' }} />,
      imageUrl: 'https://images.unsplash.com/photo-1507668077129-56e32842fceb?ixlib=rb-1.2.1&auto=format&fit=crop&w=1374&q=80',
      link: '/services/other/research'
    },
    {
      id: 'telecom',
      title: 'Managed Services for Telecom Sector',
      description: 'Specialized solutions for telecom companies to optimize operations and enhance service delivery.',
      icon: <CellTowerIcon sx={{ fontSize: 60, color: '#9c27b0' }} />,
      imageUrl: 'https://images.unsplash.com/photo-1562408590-e32931084e23?ixlib=rb-1.2.1&auto=format&fit=crop&w=1470&q=80',
      link: '/services/other/telecom'
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden' }}>
      {/* Hero Section */}
      <Box
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        sx={{
          background: 'linear-gradient(135deg, #0c2340 0%, #1a365d 100%)',
          color: 'white',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}
          >
            <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
              Other Services
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 'normal' }}>
              Specialized solutions to meet your diverse business needs
            </Typography>
          </Box>
        </Container>
        
        {/* Decorative elements */}
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          transform: 'translate(30%, 30%)',
          zIndex: 1
        }} />
        <Box sx={{
          position: 'absolute',
          top: 100,
          left: 100,
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
          borderRadius: '50%',
          zIndex: 1
        }} />
      </Box>

      {/* Services Overview */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="h4"
            component="h2"
            textAlign="center"
            fontWeight="bold"
            gutterBottom
            sx={{ mb: 2 }}
          >
            Our Specialized Services
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            textAlign="center"
            sx={{ mb: 6, maxWidth: '700px', mx: 'auto' }}
          >
            Discover our comprehensive range of specialized business solutions
          </Typography>
        </motion.div>

        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              md: 'repeat(2, 1fr)' 
            }, 
            gap: 4 
          }}
        >
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.12)',
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                <CardMedia
                  component="img"
                  height="200"
                  image={service.imageUrl}
                  alt={service.title}
                />
                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ mr: 2 }}>{service.icon}</Box>
                    <Typography gutterBottom variant="h5" component="h3" fontWeight="bold">
                      {service.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
                    {service.description}
                  </Typography>
                  <Button
                    component={Link}
                    to={service.link}
                    variant="outlined"
                    color="primary"
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      mt: 'auto',
                      borderRadius: '50px',
                      px: 3,
                      py: 1,
                      '&:hover': {
                        transform: 'translateX(5px)',
                        background: 'rgba(25, 118, 210, 0.08)',
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </Box>
      </Container>

      {/* Call to Action */}
      <Box sx={{ py: 8, background: '#f5f7fa' }}>
        <Container maxWidth="md">
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            sx={{
              textAlign: 'center',
              p: 6,
              borderRadius: '16px',
              background: 'white',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            }}
          >
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Need a Customized Solution?
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4, maxWidth: '600px', mx: 'auto' }}>
              Our team of experts is ready to help you develop tailored solutions for your specific business needs
            </Typography>
            <Button
              variant="contained"
              size="large"
              component={Link}
              to="/contact"
              endIcon={<ArrowForwardIcon />}
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: '50px',
                background: 'linear-gradient(90deg, #1976d2, #2196f3)',
                '&:hover': {
                  background: 'linear-gradient(90deg, #1565c0, #1976d2)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default OtherServices; 